import React from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./lib/auth";
import Homepage from "./components/Homepage";
import Widget from "./components/Widget";
import StatusScreen from "./components/StatusScreen";
import EmbedGenerator from "./components/EmbedGenerator";
import AdminOrders from "./components/admin/AdminOrders";
import AdminCustomers from "./components/admin/AdminCustomers";
import AdminReconciliation from "./components/admin/AdminReconciliation";
import AdminReview from "./components/admin/AdminReview";
import AdminAudit from "./components/admin/AdminAudit";
import AdminPartners from "./components/admin/AdminPartners";
import AdminWalletApprovals from "./components/admin/AdminWalletApprovals";
import AdminTreasury from "./components/admin/AdminTreasury";
import AdminRateModel from "./components/admin/AdminRateModel";
import AdminPipeline from "./components/admin/AdminPipeline";
import AdminCustomer360 from "./components/admin/AdminCustomer360";
import AdminAlerts from "./components/admin/AdminAlerts";
import AdminRefunds from "./components/admin/AdminRefunds";
import AdminReports from "./components/admin/AdminReports";
import AdminKycaid from "./components/admin/AdminKycaid";
import AdminEmail from "./components/admin/AdminEmail";
import AdminXgate from "./components/admin/AdminXgate";
import AdminIntegrations from "./components/admin/AdminIntegrations";
import AdminTeam from "./components/admin/AdminTeam";
import StaffInviteRedeem from "./components/admin/StaffInviteRedeem";
import ForgotPasswordPage from "./components/account/ForgotPasswordPage";
import ResetPasswordPage from "./components/account/ResetPasswordPage";
import ReturnFromXgate from "./components/ReturnFromXgate";
import XPayPortal from "./components/xpay/XPayPortal";
import BusinessLanding from "./components/xpay/BusinessLanding";
import CheckoutFrame from "./components/xpay/CheckoutFrame";
import CheckoutReturn from "./components/xpay/CheckoutReturn";
import AdminXpayMerchants from "./components/admin/AdminXpayMerchants";
import AdminXpayTransactions from "./components/admin/AdminXpayTransactions";
import AdminXpayWebhookLogs from "./components/admin/AdminXpayWebhookLogs";
import AdminXpayReconcile from "./components/admin/AdminXpayReconcile";
import AdminXpayFinance from "./components/admin/AdminXpayFinance";
import BusinessDocs from "./components/xpay/BusinessDocs";
import TermsPage from "./components/legal/TermsPage";
import PrivacyPage from "./components/legal/PrivacyPage";
import AmlPage from "./components/legal/AmlPage";
import WhySofPage from "./components/legal/WhySofPage";
import CookiesPage from "./components/legal/CookiesPage";
import LegalIndexPage from "./components/legal/LegalIndexPage";
import SupportPage from "./components/SupportPage";
import { AdminGuard } from "./components/admin/AdminLayout";
import PartnerLogin from "./components/partner/PartnerLogin";
import PartnerDashboard from "./components/partner/PartnerDashboard";
import PartnerAnalytics from "./components/partner/PartnerAnalytics";
import PartnerSettings from "./components/partner/PartnerSettings";
import PartnerApiKeys from "./components/partner/PartnerApiKeys";
import PartnerSandbox from "./components/partner/PartnerSandbox";
import { PartnerGuard } from "./components/partner/PartnerLayout";
import LoginPage from "./components/account/LoginPage";
import BuyCryptoLanding from "./components/buy/BuyCryptoLanding";
import BuyCryptoApp from "./components/buy/BuyCryptoApp";
import BuyReturn from "./components/buy/BuyReturn";
import CookieConsent from "./components/CookieConsent";
import AdminBuyOrders from "./components/admin/AdminBuyOrders";
import RegisterPage from "./components/account/RegisterPage";
import AccountDashboard from "./components/account/AccountDashboard";
import OrdersHistory from "./components/account/OrdersHistory";
import ReceiptPage from "./components/account/ReceiptPage";
import Wallets from "./components/account/Wallets";
import Settings from "./components/account/Settings";
import KycPage from "./components/account/KycPage";
import SofPage from "./components/account/SofPage";
import { AccountGuard } from "./components/account/AccountLayout";
import { Toaster } from "sonner";

function StatusGuard({ children }) {
  const token = typeof window !== "undefined" && localStorage.getItem("xch_token");
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <AuthProvider>
        <BrowserRouter>
          <CookieConsent />
          <Routes>
            {/* Marketing + auth */}
            <Route path="/" element={<Homepage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />

            {/* Standalone widget (embed iframe entry point + partner pre-fill) */}
            <Route path="/widget" element={<Widget />} />
            <Route path="/buy" element={<BuyCryptoApp />} />
            <Route path="/buy-crypto" element={<BuyCryptoLanding />} />
            <Route path="/buy/return" element={<BuyReturn />} />
            <Route path="/widget/status/:id" element={<StatusGuard><StatusScreen /></StatusGuard>} />

            {/* Account area */}
            <Route path="/account" element={<AccountGuard><AccountDashboard /></AccountGuard>} />
            <Route path="/account/orders" element={<AccountGuard><OrdersHistory /></AccountGuard>} />
            <Route path="/account/orders/:orderId/receipt" element={<AccountGuard><ReceiptPage /></AccountGuard>} />
            <Route path="/account/wallets" element={<AccountGuard><Wallets /></AccountGuard>} />
            <Route path="/account/settings" element={<AccountGuard><Settings /></AccountGuard>} />
            <Route path="/account/kyc" element={<AccountGuard><KycPage /></AccountGuard>} />
            <Route path="/account/sof" element={<AccountGuard><SofPage /></AccountGuard>} />

            {/* Partner area */}
            <Route path="/partner/embed" element={<EmbedGenerator />} />
            <Route path="/partner/login" element={<PartnerLogin />} />
            <Route path="/partner" element={<Navigate to="/partner/dashboard" replace />} />
            <Route path="/partner/dashboard" element={<PartnerGuard><PartnerDashboard /></PartnerGuard>} />
            <Route path="/partner/analytics" element={<PartnerGuard><PartnerAnalytics /></PartnerGuard>} />
            <Route path="/partner/settings" element={<PartnerGuard><PartnerSettings /></PartnerGuard>} />
            <Route path="/partner/api-keys" element={<PartnerGuard><PartnerApiKeys /></PartnerGuard>} />
            <Route path="/partner/sandbox" element={<PartnerGuard><PartnerSandbox /></PartnerGuard>} />

            {/* Admin area */}
            <Route path="/admin" element={<Navigate to="/admin/orders" replace />} />
            <Route path="/admin/orders" element={<AdminGuard><AdminOrders /></AdminGuard>} />
            <Route path="/admin/buy-orders" element={<AdminGuard><AdminBuyOrders /></AdminGuard>} />
            <Route path="/admin/customers" element={<AdminGuard><AdminCustomers /></AdminGuard>} />
            <Route path="/admin/customers/:id" element={<AdminGuard><AdminCustomer360 /></AdminGuard>} />
            <Route path="/admin/pipeline" element={<AdminGuard><AdminPipeline /></AdminGuard>} />
            <Route path="/admin/alerts" element={<AdminGuard><AdminAlerts /></AdminGuard>} />
            <Route path="/admin/refunds" element={<AdminGuard><AdminRefunds /></AdminGuard>} />
            <Route path="/admin/reports" element={<AdminGuard><AdminReports /></AdminGuard>} />
            <Route path="/admin/kycaid" element={<AdminGuard><AdminKycaid /></AdminGuard>} />
            <Route path="/admin/integrations" element={<AdminGuard><AdminIntegrations /></AdminGuard>} />
            <Route path="/admin/integrations/kycaid" element={<AdminGuard><AdminKycaid /></AdminGuard>} />
            <Route path="/admin/integrations/email" element={<AdminGuard><AdminEmail /></AdminGuard>} />
            <Route path="/admin/integrations/xgate" element={<AdminGuard><AdminXgate /></AdminGuard>} />
            <Route path="/admin/email" element={<AdminGuard><AdminEmail /></AdminGuard>} />
            <Route path="/admin/xgate" element={<AdminGuard><AdminXgate /></AdminGuard>} />
            <Route path="/admin/team" element={<AdminGuard><AdminTeam /></AdminGuard>} />
            {/* Public staff-invite redemption page (token in URL is the only credential) */}
            <Route path="/staff/invite/:token" element={<StaffInviteRedeem />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* XGate return-URL landing — accepts multiple aliases so we can configure
                whichever path XGate's onboarding form uses without redeploying. */}
            <Route path="/return-from-xgate" element={<ReturnFromXgate />} />
            <Route path="/xgate/return"      element={<ReturnFromXgate />} />
            <Route path="/return"            element={<ReturnFromXgate />} />
            <Route path="/checkout/complete" element={<ReturnFromXgate />} />

            {/* Xchangely Pay — merchant portal + public marketing landing.
                /instaxchange is the legacy public alias; we keep it as a 301
                redirect to /pay so old shared links don't break, but the
                provider name no longer surfaces in the URL anywhere new. */}
            <Route path="/instaxchange" element={<Navigate to="/pay" replace />} />
            <Route path="/pay" element={<XPayPortal />} />
            <Route path="/pay/checkout/:sessionId" element={<CheckoutFrame />} />
            <Route path="/pay/return" element={<CheckoutReturn />} />
            <Route path="/business" element={<BusinessLanding />} />
            <Route path="/business/docs" element={<BusinessDocs />} />
            <Route path="/admin/xpay-merchants" element={<AdminGuard><AdminXpayMerchants /></AdminGuard>} />
            <Route path="/admin/xpay-transactions" element={<AdminGuard><AdminXpayTransactions /></AdminGuard>} />
            <Route path="/admin/xpay-webhook-logs" element={<AdminGuard><AdminXpayWebhookLogs /></AdminGuard>} />
            <Route path="/admin/xpay-reconcile" element={<AdminGuard><AdminXpayReconcile /></AdminGuard>} />
            <Route path="/admin/xpay-finance" element={<AdminGuard><AdminXpayFinance /></AdminGuard>} />
            <Route path="/admin/reconciliation" element={<AdminGuard><AdminReconciliation /></AdminGuard>} />
            <Route path="/admin/review" element={<AdminGuard><AdminReview /></AdminGuard>} />
            <Route path="/admin/audit" element={<AdminGuard><AdminAudit /></AdminGuard>} />
            <Route path="/admin/partners" element={<AdminGuard><AdminPartners /></AdminGuard>} />
            <Route path="/admin/wallets" element={<AdminGuard><AdminWalletApprovals /></AdminGuard>} />
            <Route path="/admin/treasury" element={<AdminGuard><AdminTreasury /></AdminGuard>} />
            <Route path="/admin/rate-model" element={<AdminGuard><AdminRateModel /></AdminGuard>} />

            {/* Legal corpus + support — scaffold pages gated by LEGAL.REVIEWED */}
            <Route path="/legal" element={<LegalIndexPage />} />
            <Route path="/legal/terms" element={<TermsPage />} />
            <Route path="/legal/privacy" element={<PrivacyPage />} />
            <Route path="/legal/aml" element={<AmlPage />} />
            <Route path="/legal/why-sof" element={<WhySofPage />} />
            <Route path="/legal/cookies" element={<CookiesPage />} />
            <Route path="/support" element={<SupportPage />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
